import { useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { apiErrorDetail } from '@/lib/api/fraud-client'
import { useFraudPolicy, useUpdateFraudPolicy } from '@/lib/api/fraud-queries'
import type { FraudMode, FraudPolicyOverrides, FraudPolicyResponse, FraudProfile } from '@/lib/api/fraud-types'
import { usePermission } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { Banner, ModeChip, Spinner } from './shared'
import { MODE_META } from './format'

const FIELD = 'w-full rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-sm'

type NumericKey =
  | 'step_up_spend_amount' | 'step_up_reload_count' | 'step_up_distinct_cards' | 'step_up_single_amount'
  | 'step_up_velocity_count' | 'step_up_velocity_minutes' | 'step_up_score' | 'decline_score'
  | 'max_daily_card_amount' | 'transfer_lock_minutes'

const THRESHOLDS: { key: NumericKey; label: string; help: string; unit?: string }[] = [
  { key: 'step_up_reload_count', label: 'Card charges at this event', help: 'Successful card reloads and direct-pay purchases at or above which the next charge needs 3DS.' },
  { key: 'step_up_spend_amount', label: 'Cumulative card spend', help: 'Total charged to cards at this event at or above which the next charge needs 3DS.', unit: 'currency' },
  { key: 'step_up_single_amount', label: 'Single charge amount', help: 'A single charge at or above this amount needs 3DS.', unit: 'currency' },
  { key: 'step_up_velocity_count', label: 'Charges in the velocity window', help: 'Card charges within the window at or above which the next charge needs 3DS.' },
  { key: 'step_up_velocity_minutes', label: 'Velocity window', help: 'Length of the window, in minutes.', unit: 'min' },
  { key: 'step_up_distinct_cards', label: 'Distinct cards charged in 30 days', help: 'Cards charged by the account across all events in the last 30 days, the card being charged included, at or above which the next charge needs 3DS. Cards merely saved on file do not count.' },
  { key: 'step_up_score', label: 'Step-up score', help: 'Risk score (0–100) at or above which a charge needs 3DS.' },
  { key: 'decline_score', label: 'Decline score', help: 'Risk score (0–100) at or above which a charge is refused outright.' },
  { key: 'max_daily_card_amount', label: 'Daily card cap per attendee', help: 'Card charges in any 24 hours at this event may not exceed this. Blank or 0 = no cap.', unit: 'currency' },
  { key: 'transfer_lock_minutes', label: 'Transfer lock', help: 'Tokens bought by card cannot be sent to friends for this many minutes.', unit: 'min' },
]

type Draft = {
  mode: '' | FraudMode
  require_3ds_for_all: '' | 'true' | 'false'
  auto_block_on_decline: '' | 'true' | 'false'
} & Record<NumericKey, string>

function toDraft(overrides: FraudPolicyOverrides | null): Draft {
  const num = (key: NumericKey) => (overrides?.[key] == null ? '' : String(overrides[key]))
  const bool = (value: boolean | null | undefined) => (value == null ? '' : value ? 'true' : 'false')
  return {
    mode: overrides?.mode ?? '',
    require_3ds_for_all: bool(overrides?.require_3ds_for_all),
    auto_block_on_decline: bool(overrides?.auto_block_on_decline),
    step_up_spend_amount: num('step_up_spend_amount'),
    step_up_reload_count: num('step_up_reload_count'),
    step_up_distinct_cards: num('step_up_distinct_cards'),
    step_up_single_amount: num('step_up_single_amount'),
    step_up_velocity_count: num('step_up_velocity_count'),
    step_up_velocity_minutes: num('step_up_velocity_minutes'),
    step_up_score: num('step_up_score'),
    decline_score: num('decline_score'),
    max_daily_card_amount: num('max_daily_card_amount'),
    transfer_lock_minutes: num('transfer_lock_minutes'),
  }
}

/** Thresholds that are not numbers. `Number('4o')` is NaN, which JSON
 * serialises as null and the backend would save as "cleared". */
function invalidThresholds(draft: Draft): string[] {
  return THRESHOLDS.filter(({ key }) => {
    const raw = draft[key].trim()
    return raw !== '' && !/^\d+(\.\d+)?$/.test(raw)
  }).map(({ label }) => label)
}

function toPayload(draft: Draft): Partial<FraudPolicyOverrides> {
  const bool = (value: '' | 'true' | 'false') => (value === '' ? null : value === 'true')
  const payload: Partial<FraudPolicyOverrides> = {
    mode: draft.mode === '' ? null : draft.mode,
    require_3ds_for_all: bool(draft.require_3ds_for_all),
    auto_block_on_decline: bool(draft.auto_block_on_decline),
  }
  for (const { key, unit } of THRESHOLDS) {
    const raw = draft[key].trim()
    if (raw === '') {
      payload[key] = null
    } else if (unit === 'currency') {
      ;(payload as Record<string, unknown>)[key] = raw
    } else {
      ;(payload as Record<string, unknown>)[key] = Number(raw)
    }
  }
  return payload
}

export function PolicyTab({ eventId, currency }: { eventId: number; currency?: string | null }) {
  const { data, isLoading, error } = useFraudPolicy(eventId)
  if (error) return <p className="text-sm text-red-600 dark:text-red-400">Failed to load the policy: {error.message}</p>
  if (isLoading || !data) return <div className="flex items-center justify-center py-16"><Spinner /></div>
  // Remount the form when the server copy changes so its initial state is
  // always the saved policy (no state seeding in an effect).
  return <PolicyForm key={data.overrides?.updated_at ?? 'defaults'} eventId={eventId} currency={currency} data={data} />
}

function PolicyForm({ eventId, currency, data }: { eventId: number; currency?: string | null; data: FraudPolicyResponse }) {
  const canManage = usePermission('can_manage_fraud')
  const update = useUpdateFraudPolicy(eventId)
  const [draft, setDraft] = useState<Draft>(() => toDraft(data.overrides))
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  // `profile` and `profiles` arrive with the backend that introduced
  // profiles; an older backend during rollout sends neither.
  const profiles = data.profiles ?? []
  const [profile, setProfile] = useState<FraudProfile>(data.profile ?? 'standard')
  const [profileError, setProfileError] = useState<string | null>(null)

  const effective = data.effective
  const defaults = data.defaults ?? ({} as typeof data.defaults)
  // A profile picked but not applied: saving the thresholds would silently drop it.
  const profilePending = profiles.length > 0 && profile !== (data.profile ?? 'standard')
  // What a blank threshold follows: the event's profile, then the platform.
  const preset = profiles.find((p) => p.value === data.profile)?.thresholds ?? {}
  const baseline = (key: string) => (key in preset ? preset[key] : defaults[key])
  const profileLabel = profiles.find((p) => p.value === data.profile)?.label ?? data.profile ?? 'standard'

  const applyProfile = async () => {
    setProfileError(null)
    try {
      await update.mutateAsync({ profile })
    } catch (err) {
      setProfileError(apiErrorDetail(err, 'Could not apply the profile.'))
    }
  }
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }))

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setSaveError(null)
    setSaved(false)
    const invalid = invalidThresholds(draft)
    if (invalid.length) {
      setSaveError(`Not a number: ${invalid.join(', ')}.`)
      return
    }
    try {
      await update.mutateAsync(toPayload(draft))
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      setSaveError(apiErrorDetail(err, 'Could not save the policy.'))
    }
  }

  const unitLabel = (unit?: string) => (unit === 'currency' ? (currency || 'CAD') : unit === 'min' ? 'min' : '')

  return (
    <form onSubmit={submit} className="space-y-6">
      {effective.degraded && (
        <Banner kind="error">
          The fraud policy could not be read from the database. Charges are assessed in shadow on platform code defaults until it can; nothing is enforced.
        </Banner>
      )}
      {saveError && <Banner kind="error">{saveError}</Banner>}
      {saved && <Banner kind="success">Policy saved. New charges use it immediately.</Banner>}
      {!canManage && <Banner kind="info">You can view this policy. Changing it needs the manage-fraud permission.</Banner>}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-3">
            Risk profile
            <span className="text-xs font-normal text-neutral-500">{profileLabel}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {profileError && <Banner kind="error">{profileError}</Banner>}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            {profiles.map((option) => {
              const selected = profile === option.value
              return (
                <label
                  key={option.value}
                  className={cn('flex flex-col gap-1 p-3 rounded-lg border cursor-pointer text-sm',
                    selected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-neutral-200 dark:border-neutral-800',
                    !canManage && 'cursor-default opacity-80')}
                >
                  <span className="flex items-center gap-2 font-medium text-neutral-900 dark:text-white">
                    <input type="radio" name="profile" value={option.value} checked={selected} disabled={!canManage} onChange={() => setProfile(option.value)} />
                    {option.label.split(' — ')[0]}
                  </span>
                  <span className="text-xs text-neutral-600 dark:text-neutral-400">{option.description}</span>
                </label>
              )
            })}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {canManage && profile !== data.profile && (
              <Button type="button" size="sm" onClick={applyProfile} disabled={update.isPending}>
                {update.isPending ? 'Applying…' : `Apply ${profiles.find((p) => p.value === profile)?.label.split(' — ')[0] ?? profile}`}
              </Button>
            )}
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              A profile is a starting point: applying one fills in the thresholds below, and you adjust any of them from there (the profile becomes Custom, the other values stay).
              An organizer who accepts more risk picks Relaxed; a known attendee or card is better handled on the Trusted tab.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-3">
            Mode <ModeChip mode={effective.mode} />
            {!data.overrides?.mode && <span className="text-xs font-normal text-neutral-500">platform default</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            {(['', ...(data.modes ?? [])] as ('' | FraudMode)[]).map((mode) => {
              const meta = mode ? MODE_META[mode] : null
              const selected = draft.mode === mode
              return (
                <label
                  key={mode || 'default'}
                  className={cn('flex flex-col gap-1 p-3 rounded-lg border cursor-pointer text-sm',
                    selected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-neutral-200 dark:border-neutral-800',
                    !canManage && 'cursor-default opacity-80')}
                >
                  <span className="flex items-center gap-2 font-medium text-neutral-900 dark:text-white">
                    <input type="radio" name="mode" value={mode} checked={selected} disabled={!canManage} onChange={() => set('mode', mode)} />
                    {meta ? meta.label : `Platform default (${String(defaults.mode)})`}
                  </span>
                  <span className="text-xs text-neutral-600 dark:text-neutral-400">
                    {meta ? meta.description : "Follow the platform setting, EventPay's rollout switch. Pick a mode here to override it for this event."}
                  </span>
                </label>
              )
            })}
          </div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Blocks are enforced in every mode. Enforce needs Evervault 3-D Secure on the platform and the current mobile app release; the server refuses it otherwise.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Step-up and refusal thresholds</CardTitle></CardHeader>
        <CardContent>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">
            Adjust freely. A blank field follows the {profileLabel.split(' — ')[0]} profile (value shown beside it). Enter 0 to switch a trigger off for this event.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {THRESHOLDS.map(({ key, label, help, unit }) => (
              <label key={key} className="text-sm">
                <span className="block mb-1 font-medium text-neutral-800 dark:text-neutral-200">{label}</span>
                <div className="flex items-center gap-2">
                  <input
                    className={FIELD}
                    inputMode="decimal"
                    disabled={!canManage}
                    value={draft[key]}
                    placeholder={baseline(key) == null ? 'off' : String(baseline(key))}
                    onChange={(e) => set(key, e.target.value)}
                  />
                  {unitLabel(unit) && <span className="text-xs text-neutral-500 w-10">{unitLabel(unit)}</span>}
                </div>
                <span className="block mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  {help} In effect now: <strong>{effective[key] == null ? 'off' : String(effective[key])}</strong>.
                </span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Switches</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {([
            ['require_3ds_for_all', 'Challenge every card charge', 'Every card-not-present charge needs 3DS, whatever the score. For an event under active attack.'],
            ['auto_block_on_decline', 'Block on refusal', 'A charge refused by the risk policy places an event-level block on the attendee automatically.'],
          ] as const).map(([key, label, help]) => (
            <label key={key} className="text-sm">
              <span className="block mb-1 font-medium text-neutral-800 dark:text-neutral-200">{label}</span>
              <select className={FIELD} disabled={!canManage} value={draft[key]} onChange={(e) => set(key, e.target.value as Draft[typeof key])}>
                <option value="">Default ({baseline(key) ? 'on' : 'off'})</option>
                <option value="true">On</option>
                <option value="false">Off</option>
              </select>
              <span className="block mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                {help} In effect now: <strong>{effective[key] ? 'on' : 'off'}</strong>.
              </span>
            </label>
          ))}
        </CardContent>
      </Card>

      {canManage && (
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={update.isPending || profilePending}>{update.isPending ? 'Saving…' : 'Save policy'}</Button>
          {profilePending && (
            <span className="text-xs text-amber-700 dark:text-amber-400">Apply the selected profile first, or pick {profileLabel} again to keep it.</span>
          )}
          <Button type="button" variant="outline" onClick={() => setDraft(toDraft(data.overrides))} disabled={update.isPending}>Reset</Button>
          {data.overrides?.updated_at && (
            <span className="text-xs text-neutral-500 dark:text-neutral-400">Last changed {new Date(data.overrides.updated_at).toLocaleString()}</span>
          )}
        </div>
      )}
    </form>
  )
}
